import * as React from 'react'

import { getBrandedMapPinSrc, getPropertyMarkerFrameSize, getPropertyMarkerPin } from './pinsV2'
import { OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import useRebrandVersion from 'hooks/useRebrandVersion'
import styled, { keyframes } from 'styled-components'
import PinHeart from 'tripPlanner/components/TripMaps/MapElement/PinAccessory/PinHeart'
import { rem } from 'polished'
import clsx from 'clsx'
const PinHeartContainer = styled.div`
  position: absolute;
  z-index: 99;

  // flash and default have same positioning for pin heart
  bottom: ${rem(40)};
  /* oxlint-disable-next-line logical-css/no-physical-properties */
  left: ${rem(8)};

  &.isHovered, &.active {
    bottom: ${rem(44)};
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    left: ${rem(12)};
  }

  &.soldOut {
    top: ${rem(-24)};
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    left: ${rem(6)};

    &.isHovered, &.active {
      top: ${rem(-28)};
      /* oxlint-disable-next-line logical-css/no-physical-properties */
      left: ${rem(8)};
    }
  }

  &.isAllBedbank {
    bottom: ${rem(28)};
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    left: ${rem(6)};

    &.isHovered, &.active {
      bottom: ${rem(32)};
      /* oxlint-disable-next-line logical-css/no-physical-properties */
      left: ${rem(8)};
    }
  }
`

const pinGrowAnimation = keyframes`
  from {
    transform: scale(0.5);
  }
  to {
    transform: scale(1);
  }
`

const MarkerContainer = styled.div`
  filter: drop-shadow(0px 3px 3px rgba(0, 0, 0, 0.4));
  position: relative;
  width: var(--property-marker-width);
  height: var(--property-marker-height);
`

const AnchorLayer = styled.div`
  position: absolute;
  /* oxlint-disable-next-line logical-css/no-physical-properties */
  left: var(--property-marker-anchor-left);
  top: var(--property-marker-anchor-top);
  width: 0;
  height: 0;
`

const PinImage = styled.img`
  position: absolute;

  &.animate {
    animation: ${pinGrowAnimation} 0.2s ease-out;
    transform-origin: bottom center;
  }
`

interface Props {
  active: boolean
  visited: boolean
  soldOut: boolean
  marker: App.MapMarkerV2
  isHovered?: boolean
  isSaved?: boolean
  animateMarker?: boolean
}

function PropertyMarker(props: React.PropsWithChildren<Props>) {
  const {
    active,
    visited,
    soldOut,
    isSaved,
    marker,
    isHovered,
    animateMarker = false,
  } = props

  const isAllBedbank = marker.offers.every(offer => offer.type === OFFER_TYPE_BED_BANK)

  const isAllFlash = marker.offers.every(offer => offer.type === OFFER_TYPE_HOTEL)
  const pin = getPropertyMarkerPin(marker, { active, visited, soldOut, isHovered })
  const { image, sizing, brandedPin } = pin
  const isRebrand = useRebrandVersion() === 'rebrand'
  const pinSrc = getBrandedMapPinSrc(image, brandedPin, isRebrand)

  const [width, height] = sizing.size
  const [, frameHeight] = getPropertyMarkerFrameSize(sizing)
  const [anchorLeft, anchorTop] = sizing.anchor
  const markerContainerStyle = React.useMemo(() => ({
    '--property-marker-width': rem(width),
    '--property-marker-height': rem(frameHeight),
  }) as React.CSSProperties, [frameHeight, width])
  const anchorLayerStyle = React.useMemo(() => ({
    '--property-marker-anchor-left': rem(anchorLeft),
    '--property-marker-anchor-top': rem(anchorTop),
  }) as React.CSSProperties, [anchorLeft, anchorTop])
  const pinStyle = React.useMemo(() => ({
    height: rem(height),
    width: rem(width),
    // oxlint-disable-next-line logical-css/no-physical-properties
    left: rem(-anchorLeft),
    top: rem(-anchorTop),
  }), [anchorLeft, anchorTop, height, width])
  const pinClassName = clsx('map-pin', { animate: animateMarker })

  return (
    <MarkerContainer className={clsx('marker-container')} style={markerContainerStyle}>
      <AnchorLayer className={clsx('anchor-layer')} style={anchorLayerStyle}>
        {
          isSaved && (
            <PinHeartContainer className={clsx({ isAllBedbank, isAllFlash, soldOut, isHovered, active })}>
              <PinHeart />
            </PinHeartContainer>
          )
        }
        <PinImage
          className={pinClassName}
          src={pinSrc}
          alt=""
          style={pinStyle}
        />
      </AnchorLayer>
    </MarkerContainer>
  )
}

export default React.memo(PropertyMarker)
